import {
  LessonData,
  LessonPhase,
  SchoolLevel,
  SchoolType,
  defaultGroupOrder,
  getGroupNumberFromOrder,
  defaultGroup,
  Differentiation,
  Pin,
} from '@lessonup/pins-shared';
import { Dictionary, isNil, isUndefined, forEach, keyBy, first, map, isEmpty } from 'lodash';
import { Identifiable, Modifiable, Nullable, omitDeepUndefined, Ownable } from '../../utils';
import { FractionalIndex } from '../common';
import { AssignmentComplete, AssignmentType } from './AssignmentComplete';
import { AssignmentPin } from './AssignmentPin';
import { AssignmentUtils } from './AssignmentUtils';

// TODO: this should be modelled in ZOD and moved to packages/published/assignments-shared/src/assignment
export interface AssignmentContent<T extends AssignmentType = AssignmentType>
  extends Ownable,
    Identifiable,
    Modifiable {
  type: T;
  name: string;
  channel: string | undefined;
  message?: string;
  subject?: string;
  groupId: string | undefined;
  module: string | undefined;
  pins: {
    [key: string]: AssignmentPin;
  };
  diff?: Differentiation[];
  language?: string;

  durationInMin: number | undefined;
  schoolType: SchoolType[] | undefined;
  levels: SchoolLevel[] | undefined;
  years: number[] | undefined;
  country: string[] | undefined;
  themes: string[] | undefined;

  showPhases: boolean;
  phases: LessonPhase[] | undefined;
  products: string[] | undefined | null;

  testPoints?: number;
  origin: AssignmentOrigin;

  lessonId?: string;
  version?: 'v2';
}

type AssignmentOrigin = 'link' | 'group' | undefined;

export namespace AssignmentContent {
  export type VisibilityState = 'all' | 'studentOnly';
  // export type UpdateData = Omit<AssignmentContent, '_id' | 'creationDate'>;
  export const excludedComparisonPaths: string[] = ['createdAt', 'modifiedAt'];
}

export namespace AssignmentContent {
  export type Realtime = AssignmentContent<'realtime'>;

  export interface TimeOptions {
    startDate?: Date;
    closeDate?: Date;
    duration?: number;
  }

  export interface Options extends TimeOptions {
    subject: string | undefined;
    groupId: string | undefined;
    module: string | undefined;
    order: FractionalIndex.OrderKey | undefined;
    origin: AssignmentOrigin;
    message: string | undefined;
    language: string | undefined;
    label: string | undefined;
    legacy?: boolean;
    showFinalSlide?: boolean;
    restrictToGroup?: boolean;
  }

  export interface UpdateData {
    ownerId?: string;
    testPoints?: number;
    pins?: PinUpdateData[];
  }

  export type PinUpdateData = {
    pinId: string;
  } & Nullable<AssignmentPin.UpdateData>;

  export function updateFields(data: UpdateData): object {
    const updateFields: Dictionary<any> = {};

    if (!isNil(data.testPoints)) updateFields['testPoints'] = data.testPoints;
    if (!isNil(data.ownerId)) updateFields['ownerId'] = data.ownerId;

    forEach(data.pins, (pin) => {
      const pinId = pin.pinId;
      //pin
      if (!isUndefined(pin.order)) updateFields[`pins.${pinId}.order`] = pin.order;
      if (!isUndefined(pin.testPoints)) updateFields[`pins.${pinId}.item.testPoints`] = pin.testPoints;
    });

    const cleaned = omitDeepUndefined(updateFields);
    if (!isEmpty(cleaned)) {
      cleaned['modifiedAt'] = Date.now();
    }
    return cleaned;
  }

  export function createFromLesson<T extends AssignmentType = AssignmentType>(
    assignmentId: string,
    ownerId: string,
    type: T,
    lessonData: LessonData,
    createdAt: number,
    options: Options
  ): AssignmentContent<T> {
    const assignmentPins = lessonData.pins.map((pin) => AssignmentPin.assignmentPinFromTeacherPin(pin));
    const pins = AssignmentUtils.excludeUnsupportedData(assignmentPins);

    const pinDict = keyBy(pins, (p) => p._id);
    const { name, differentation, country, durationInMin, schoolType, levels, years, themes, channel } =
      lessonData.lesson;

    const content: AssignmentContent<T> = {
      _id: assignmentId,
      type,
      name: name || '',
      createdAt,
      modifiedAt: createdAt,
      ownerId,
      diff: differentation || undefined,
      pins: pinDict,
      subject: options.subject || first(lessonData.lesson.subjects),
      groupId: options.groupId,
      module: options.module,
      message: options.message,
      language: options.language,
      origin: options.origin,
      showPhases: lessonData.lesson.showPhases === true,
      phases: map(lessonData.lesson.phases, (phase) => ({
        label: phase.label || '',
        subtitle: !phase.subtitle ? undefined : phase.subtitle,
      })),
      products: !isEmpty(lessonData.lesson.products) ? lessonData.lesson.products : undefined,
      country,
      durationInMin: durationInMin || undefined,
      schoolType: schoolType || undefined,
      levels: levels || undefined,
      years: years || undefined,
      themes,
      channel: channel || undefined,
      // TODO: Something with tests
      // testPoints: type === 'test' ? sumBy(pins, (pin) => pin.settings.testPoints || 0) : undefined,
      testPoints: undefined,
      // NOTE: This should always be "v2" after the revamp of the homework with the new lesson data model.
      version: lessonData.lesson.version, // undefined for classic lessons and "v2" for V2 lessons.
    };

    return content;
  }

  export function updateFromLesson<T extends AssignmentType = AssignmentType>(
    old: AssignmentContent<T>,
    lessonData: LessonData,
    options: Options
  ): AssignmentContent<T> {
    const updated = createFromLesson(old._id, old.ownerId, old.type, lessonData, old.createdAt, options);
    return {
      ...updated,
    };
  }

  export function optionsFromContent(data: AssignmentComplete): AssignmentContent.Options {
    const { content, meta } = data;
    return {
      groupId: content.groupId,
      order: meta.order,
      module: content.module,
      subject: content.subject,
      message: content.message,
      label: meta.label,
      language: content.language,
      origin: content.origin,
    };
  }

  export function isRealtime(assignment: AssignmentContent): assignment is AssignmentContent.Realtime {
    return assignment.type == 'realtime';
  }

  export function pinsInPhases(phases: LessonPhase[], pins: Pin[]): { [key: string]: Pin[] } {
    const phaseNameMap = phases.map((phase) => phase.label);

    const phaseNames = phaseNameMap.reduce((acc: Dictionary<Pin[]>, label) => {
      acc[label] = [];
      return acc;
    }, {});

    return pins.reduce((acc: Dictionary<Pin[]>, pin) => {
      const order = pin.order || defaultGroupOrder;

      if (order.startsWith(defaultGroup)) {
        acc[phaseNameMap[0]].push(pin);
      } else {
        const orderIndex = getGroupNumberFromOrder(order) - 1;
        acc[phaseNameMap[orderIndex]].push(pin);
      }

      return acc;
    }, phaseNames);
  }

  export function firstPhaseWithPins(phasePins: { [key: string]: Pin[] }): string | undefined {
    const arrayEntry = Object.entries(phasePins).find(([, value]) => value.length > 0);
    if (!arrayEntry) return;

    return arrayEntry[0];
  }
}
